import { DateTime } from 'luxon';
import { NEVER_EXPIRES, SubscriptionStatus } from './couch-user';
import type { CouchUserCoachSeatData, CouchUserCoachTierData } from './couch-user';
import type { CouchUserCoachBilling } from './coach-billing';
import { expiresMillis, isInGrace } from './license-reconciler';

/**
 * The horizon beyond which a mirrored expiry is suspicious (#4361 Phase 1).
 *
 * Coach plans are monthly-only today: the Paddle coach seat has a single
 * configured price with no yearly variant, and the dormant app-store tiers
 * differ by client-slot cap, not by billing period. Both writers set the expiry
 * to the period end plus at most a 24-hour grace, so a coach block can never
 * carry an expiry more than about 32 days out.
 *
 * That monthly-only assumption is load-bearing: it is what bounds how far a
 * STALE mirror can leak if every refresh mechanism missed. 40 days leaves room
 * for the grace and for clock skew while still being unreachable today, so if
 * this ever trips, a longer coach billing period has appeared and the leak
 * window has grown with it.
 */
export const COACH_MIRROR_EXPIRY_HORIZON_DAYS = 40;

/**
 * The parts of a coach's user record that decide what their clients get.
 *
 * Structural on purpose rather than taking a whole `CouchUserInfo`: the server
 * holds the `_users` record while the client holds a `CouchLoginResult`, and
 * both satisfy this shape.
 *
 * `coachBillingData` is NOT present on `CouchLoginResult`, and that is
 * deliberately harmless rather than a gap to close. Rules 6 and 7 below both
 * return `no-rail`, and rule 5 already fires ahead of both whenever a tier or
 * seat block exists — so a caller passing it as `undefined` gets an identical
 * answer in every case. Do not "fix" this by exposing `coachBillingData` on the
 * login result.
 */
export interface CoachRailBlocks {
  coachTierData?: CouchUserCoachTierData;
  coachSeatData?: CouchUserCoachSeatData;
  coachBillingData?: CouchUserCoachBilling;
  coachMaxSlots?: number;
}

/**
 * What a coach's clients should be holding right now.
 *
 * Four outcomes, not a nullable pair, because the callers must treat them
 * differently. `no-rail` means "restore `NEVER_EXPIRES`" — a Legacy coach has
 * no subscription that can lapse — while `coach-missing` means "revoke".
 * Collapsing the two is what left a deleted coach's clients granted forever in
 * an earlier draft of this design.
 *
 * The first three answer a question about the COACH, and so are the same for
 * every client that coach has. `client-asleep` is the one per-client outcome:
 * it is produced by {@link resolveForClient}, never by
 * {@link resolveCoachLicenseMirror}.
 */
export type CoachLicenseMirror =
  | { kind: 'mirror'; subscriptionStatus: SubscriptionStatus; expiresDate_utc: string }
  /** The coach has no subscription that can lapse (Legacy / dead metered rail). */
  | { kind: 'no-rail' }
  /** The coach's user record is gone entirely (deleted account). */
  | { kind: 'coach-missing' }
  /**
   * This ONE client has been put to sleep by their coach (#4530). The coach is
   * fine and their other clients are unaffected; this client alone holds no
   * coach-granted access until they are woken.
   *
   * Treated exactly like `coach-missing` everywhere it is consumed, because
   * both mean the same thing to a client: revoke, effective now. The two are
   * kept apart anyway so that logs and future callers can tell "your coach's
   * account is gone" from "your coach parked you", which are very different
   * things to a person even though the access outcome is identical.
   */
  | { kind: 'client-asleep' };

/**
 * Narrow the coach-level answer down to ONE client (#4530).
 *
 * {@link resolveCoachLicenseMirror} answers a question about the coach — "what
 * should this coach's clients be holding right now?" — and gives the same
 * answer for the whole roster. Sleeping is per-client, so the answer has to be
 * narrowed for one client without disturbing the coach-level one.
 *
 * Keeping this a separate pure step, rather than teaching the coach-level
 * resolver about individual clients, is what preserves #4361's guarantee that
 * an AWAKE client's access is a word-for-word copy of their coach's.
 */
export function resolveForClient(
  coachLevel: CoachLicenseMirror,
  clientIsAsleep: boolean,
): CoachLicenseMirror {
  return clientIsAsleep ? { kind: 'client-asleep' } : coachLevel;
}

/** Optional hooks, so the pure function stays pure and the caller decides how
 *  to report. */
export interface ResolveCoachLicenseMirrorOptions {
  /** Called when a coach block's expiry had to be sanitised, or when the
   *  mirrored expiry sits beyond {@link COACH_MIRROR_EXPIRY_HORIZON_DAYS}. */
  onCanary?: (message: string) => void;
}

/**
 * Of the two coach blocks, the one whose expiry is later — comparing with
 * `expiresMillis`, so `'never'` counts as `+Infinity` and a missing expiry as
 * `-Infinity`. At least one of the two must be present; the caller guarantees
 * that. Ties go to the tier block, matching the head of
 * `resolveCoachCapability`'s order.
 */
function pickLaterExpiringBlock(
  tier: CouchUserCoachTierData | undefined,
  seat: CouchUserCoachSeatData | undefined,
): CouchUserCoachTierData | CouchUserCoachSeatData {
  if (!seat) return tier!;
  if (!tier) return seat;
  return expiresMillis(tier.expiresDate_utc) >= expiresMillis(seat.expiresDate_utc) ? tier : seat;
}

/**
 * Resolve what a coach's clients should be holding, from the coach's own record.
 *
 * The result is mirrored onto each client's `coachData` **verbatim** — same
 * status, same expiry — so `isInGrace` gives a client's block exactly the same
 * answer it gives the coach's own. The client's access is therefore identical to
 * the coach's by construction, with no derived rule that could drift.
 *
 * ## Precedence, in this exact order
 *
 * 1. `coach` is null/absent → `coach-missing`.
 * 2. `coachTierData` present and in grace → mirror it.
 * 3. `coachSeatData` present and in grace → mirror it.
 * 4. `coachMaxSlots > 0` → `no-rail`. **Agrees with `resolveCoachCapability`,
 *    and sits above rule 5 in order to keep agreeing with it.** An
 *    admin-granted Legacy coach may coach unconditionally, so a tier they once
 *    trialled and let lapse must not revoke their clients.
 * 5. `coachTierData` or `coachSeatData` present but lapsed → mirror whichever
 *    has the later expiry (the coach's real last entitlement end).
 * 6. `coachBillingData` present → `no-rail` (the dead PayPro metered rail).
 *    **This is the one genuine disagreement with `resolveCoachCapability`**,
 *    which checks `coachBillingData` ahead of everything below it and would
 *    call this coach `Metered`. Here it sits BELOW rule 5, so a Paddle seat
 *    coach carrying residual `coachBillingData` from the PPG migration is
 *    judged on their lapsed seat rather than treated as a dead-rail coach.
 *    Treating them as Metered would mean "no subscription to lapse" and hand
 *    their clients `NEVER_EXPIRES` — a permanent grant, which is precisely the
 *    leak #4361 exists to close.
 * 7. otherwise → `no-rail`.
 *
 * So rules 4 and 6 straddle rule 5 for opposite reasons: rule 4 to preserve the
 * resolver's verdict, rule 6 to overturn it. Both placements are pinned by tests.
 *
 * This deliberately does NOT delegate to `resolveCoachCapability`. That resolver
 * has no lapsed-block branch at all, so it cannot express the question this one
 * has to answer.
 *
 * ## Safe to evaluate at a FUTURE time
 *
 * The `now` parameter is what makes that work: a caller can ask "what will these
 * clients have once the current block expires?" by passing a `now` just past
 * that expiry. Every rule keys on `isInGrace(…, now)`, so nothing extra is
 * needed — but do NOT cache a captured `DateTime.utc()` inside this function, or
 * that property breaks silently.
 *
 * Pure and side-effect free: Luxon and `@cf-shared` only, no I/O, Angular-free,
 * so both the client and the server can call it.
 */
export function resolveCoachLicenseMirror(
  coach: CoachRailBlocks | null | undefined,
  now?: DateTime,
  options?: ResolveCoachLicenseMirrorOptions,
): CoachLicenseMirror {
  const at = now ?? DateTime.utc();
  const atMillis = at.toMillis();

  // rule 1: the coach's record is gone entirely.
  if (!coach) return { kind: 'coach-missing' };

  const tier = coach.coachTierData;
  const seat = coach.coachSeatData;

  // rule 2: an active fixed tier backs the coach.
  if (tier && isInGrace(tier.subscriptionStatus, tier.expiresDate_utc, atMillis)) {
    return mirrorGrantingBlock(tier.subscriptionStatus, tier.expiresDate_utc, at, options);
  }

  // rule 3: an active Paddle seat backs the coach.
  if (seat && isInGrace(seat.subscriptionStatus, seat.expiresDate_utc, atMillis)) {
    return mirrorGrantingBlock(seat.subscriptionStatus, seat.expiresDate_utc, at, options);
  }

  // rule 4: an admin-granted Legacy coach has nothing that can lapse. Above
  // rule 5 on purpose — a lapsed tier they once trialled must not revoke.
  if ((coach.coachMaxSlots ?? 0) > 0) return { kind: 'no-rail' };

  // rule 5: a lapsed rail still decides. Pick whichever block ran out LAST —
  // that is the coach's real last entitlement end — and copy that one block's
  // status and expiry together, so the pair always comes from the same source.
  if (tier || seat) {
    const lastLapsedBlock = pickLaterExpiringBlock(tier, seat);
    return buildMirror(lastLapsedBlock.subscriptionStatus, lastLapsedBlock.expiresDate_utc, at, options);
  }

  // rule 6: residual metered-rail data, with no tier or seat block beside it.
  // rule 7: nothing at all. Both mean "no subscription that can lapse".
  return { kind: 'no-rail' };
}

/**
 * Mirror a coach block that is currently GRANTING (rules 2 and 3).
 *
 * The one special case is a granting block whose expiry is `'never'`. That
 * coach has no date at which anything lapses, and the rest of the app agrees
 * they are fully active — `coach.test.ts` pins exactly this shape, with
 * `effectiveCoachSlots` returning the tier cap and `hasActiveCoachSeat`
 * returning true. So the honest answer is `no-rail`: there is no subscription
 * that can lapse, and the clients keep `NEVER_EXPIRES` just as they do under a
 * Legacy coach.
 *
 * Sanitising it to an already-past date instead — the fail-closed reflex —
 * would break the whole design's invariant, by revoking every client of a coach
 * the app still considers active. The client's access is supposed to be
 * identical to the coach's; here that means permanent, because the coach's is.
 *
 * The canary still fires, because a `'never'` on a coach block is not something
 * the billing rails can currently produce, and whoever wrote it should be told.
 * A NON-granting block carrying `'never'` needs none of this: rule 5 mirrors it
 * verbatim, and a non-granting status means no access whatever the date says.
 */
function mirrorGrantingBlock(
  status: SubscriptionStatus,
  expiry: string | undefined,
  at: DateTime,
  options?: ResolveCoachLicenseMirrorOptions,
): CoachLicenseMirror {
  if (expiry === NEVER_EXPIRES) {
    options?.onCanary?.(
      `[PROBLEM] coach-license-mirror: a GRANTING coach block carries '${NEVER_EXPIRES}' — treating the coach as having no lapsing subscription rather than revoking their clients`,
    );
    return { kind: 'no-rail' };
  }
  return buildMirror(status, expiry, at, options);
}

/**
 * Build a `mirror` result with a defined, finite expiry.
 *
 * A mirrored expiry must never be `undefined` and must never be `'never'`.
 * The `'never'` case matters more than the undefined one: the whole
 * stale-mirror safety argument rests on `NEVER_EXPIRES` never reaching a coach
 * block, but that is an unenforced assumption sitting one line from a permanent
 * grant fanned out to every client. `activateThrough` already branches on
 * `current === 'never'`, and `coach.test.ts` pins a `'never'` tier shape as
 * supported, so it is reachable. Substituting `now` yields an already-expired
 * date — the fail-closed direction — and logs the canary.
 */
function buildMirror(
  status: SubscriptionStatus,
  expiry: string | undefined,
  at: DateTime,
  options?: ResolveCoachLicenseMirrorOptions,
): CoachLicenseMirror {
  const nowIso = at.toISO()!;
  if (expiry === undefined || expiry === NEVER_EXPIRES) {
    options?.onCanary?.(
      `[PROBLEM] coach-license-mirror: coach block carried ${expiry === undefined ? 'no expiry' : `'${NEVER_EXPIRES}'`} — substituting ${nowIso} rather than granting forever`,
    );
    return { kind: 'mirror', subscriptionStatus: status, expiresDate_utc: nowIso };
  }
  const horizon = at.plus({ days: COACH_MIRROR_EXPIRY_HORIZON_DAYS });
  if (expiresMillis(expiry) > horizon.toMillis()) {
    options?.onCanary?.(
      `[PROBLEM] coach-license-mirror: expiry horizon exceeds monthly assumption — ${expiry} is more than ${COACH_MIRROR_EXPIRY_HORIZON_DAYS} days after ${nowIso}`,
    );
  }
  return { kind: 'mirror', subscriptionStatus: status, expiresDate_utc: expiry };
}
